import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Filtros y página en la URL (se pueden compartir y sobreviven a recargar). Los valores vacíos y
 * la página 1 no se escriben. Cambiar un filtro regresa a la página 1.
 */
export function useFiltrosUrl<K extends string>(claves: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const filtros = useMemo(() => {
    const out = { page: Number(params.get('page') ?? '1') || 1 } as Record<K, string> & {
      page: number;
    };
    claves.forEach((k) => {
      (out as Record<string, string | number>)[k] = params.get(k) ?? '';
    });
    return out;
  }, [params, claves]);

  const cambiar = useCallback(
    (cambios: Partial<Record<K, string>> & { page?: number }) => {
      const next: Record<string, string | number> = { ...filtros, page: 1, ...cambios };
      const p = new URLSearchParams();
      Object.entries(next).forEach(([k, v]) => {
        if (v !== '' && !(k === 'page' && v === 1)) p.set(k, String(v));
      });
      setParams(p, { replace: true });
    },
    [filtros, setParams],
  );

  /** Solo los filtros con valor, listos para `params.query`. */
  const query = useMemo(() => {
    const q: Record<string, string> = {};
    claves.forEach((k) => {
      if (filtros[k] !== '') q[k] = filtros[k];
    });
    return q;
  }, [filtros, claves]);

  return { filtros, cambiar, query };
}
